import client from '../../api/client';

export type CalendarPriority = 'high' | 'medium' | 'low';
export type CalendarRepeatType = 'none' | 'daily' | 'weekly' | 'monthly' | 'custom';

export type CalendarTaskLabel = {
  id: string;
  calendarId?: string;
  name: string;
  color: string;
};

export type CalendarChecklistItem = {
  id: string;
  text: string;
  isChecked: boolean;
};

export type CalendarChecklist = {
  id: string;
  title: string;
  items: CalendarChecklistItem[];
};

export type CalendarAttachment = {
  id: string;
  name: string;
  url?: string;
};

export type CalendarTask = {
  id: string;
  calendarId: string;
  title: string;
  description?: string;
  durationMinutes: number;
  startAt: string;
  deadlineAt?: string;
  repeatType: CalendarRepeatType;
  customRepeatDays: string[];
  priority?: CalendarPriority;
  labelIds: string[];
  headerColor?: string;
  checklists: CalendarChecklist[];
  attachments: CalendarAttachment[];
  isCompleted: boolean;
  sourceBoardCardId?: number;
  recurrenceParentId?: string;
  recurrenceExceptionDates?: string[];
  recurrenceEndAt?: string;
  createdAt: string;
  updatedAt: string;
};

export type CalendarTaskOccurrence = CalendarTask & {
  occurrenceKey: string;
  parentTaskId: string;
  isRecurringOccurrence: boolean;
  occurrenceDate: string;
};

export type CalendarTaskOccurrenceOverride = {
  id: string;
  calendarId: string;
  taskId: string;
  occurrenceDate: string;
  data: Partial<Pick<
    CalendarTask,
    | 'title'
    | 'description'
    | 'durationMinutes'
    | 'startAt'
    | 'deadlineAt'
    | 'priority'
    | 'labelIds'
    | 'headerColor'
    | 'checklists'
    | 'attachments'
    | 'isCompleted'
  >>;
  isDeleted?: boolean;
  createdAt: string;
  updatedAt: string;
};

const TASKS_KEY = 'boardly.calendar.tasks';
const LABELS_KEY = 'boardly.calendar.labels';
const OVERRIDES_KEY = 'boardly.calendar.taskOverrides';

const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const canUseCalendarApi = () => {
  if (typeof window === 'undefined') return false;
  return Boolean(window.localStorage.getItem('authToken'));
};

const readArray = <T,>(key: string): T[] => {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeArray = <T,>(key: string, value: T[]) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(value));
};

export const getCalendarTaskSeriesId = (task: Pick<CalendarTask, 'id'> | Pick<CalendarTaskOccurrence, 'id' | 'parentTaskId'>) => {
  const occurrence = task as Partial<CalendarTaskOccurrence>;
  return occurrence.parentTaskId || task.id.split('::')[0];
};

const replaceCalendarItems = <T extends { calendarId: string }>(key: string, calendarId: string, items: T[]) => {
  const existingItems = readArray<T>(key);
  writeArray(key, [...items, ...existingItems.filter(item => item.calendarId !== calendarId)]);
};

type CalendarTaskSaveInput = Omit<CalendarTask, 'id' | 'calendarId' | 'createdAt' | 'updatedAt'> & { id?: string };

const toCalendarTaskPayload = (calendarId: string, task: Partial<CalendarTask>) => {
  const {
    calendarId: _calendarId,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    ...taskPayload
  } = task;
  return { ...taskPayload, calendarId };
};

const sanitizeOccurrenceOverrideData = (
  data: CalendarTaskOccurrenceOverride['data']
): CalendarTaskOccurrenceOverride['data'] => {
  const {
    id: _id,
    calendarId: _calendarId,
    recurrenceParentId: _recurrenceParentId,
    recurrenceExceptionDates: _recurrenceExceptionDates,
    recurrenceEndAt: _recurrenceEndAt,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    ...safeData
  } = data as Partial<CalendarTask>;
  return safeData;
};

export const getCalendarTasks = (calendarId: string): CalendarTask[] => {
  return readArray<CalendarTask>(TASKS_KEY).filter(task => task.calendarId === calendarId);
};

export const saveCalendarTask = (
  calendarId: string,
  task: CalendarTaskSaveInput
): CalendarTask => {
  const now = new Date().toISOString();
  const tasks = readArray<CalendarTask>(TASKS_KEY);
  const existing = task.id ? tasks.find(item => item.id === task.id) : undefined;
  const nextTask: CalendarTask = {
    ...task,
    id: task.id || makeId(),
    calendarId,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
  };
  writeArray(TASKS_KEY, [nextTask, ...tasks.filter(item => item.id !== nextTask.id)]);
  return nextTask;
};

export const updateCalendarTask = (calendarId: string, taskId: string, data: Partial<CalendarTask>): CalendarTask | null => {
  const tasks = readArray<CalendarTask>(TASKS_KEY);
  const existing = tasks.find(task => task.calendarId === calendarId && task.id === taskId);
  if (!existing) return null;
  const nextTask = { ...existing, ...data, updatedAt: new Date().toISOString() };
  writeArray(TASKS_KEY, tasks.map(task => task.id === taskId ? nextTask : task));
  return nextTask;
};

export const deleteCalendarTask = (calendarId: string, taskId: string) => {
  const tasks = readArray<CalendarTask>(TASKS_KEY);
  writeArray(TASKS_KEY, tasks.filter(task => !(task.calendarId === calendarId && task.id === taskId)));
};

export const addCalendarTaskExceptionDate = (calendarId: string, taskId: string, dateKey: string) => {
  const tasks = readArray<CalendarTask>(TASKS_KEY);
  const existing = tasks.find(task => task.calendarId === calendarId && task.id === taskId);
  if (!existing) return null;
  const nextTask = {
    ...existing,
    recurrenceExceptionDates: Array.from(new Set([...(existing.recurrenceExceptionDates || []), dateKey])),
    updatedAt: new Date().toISOString(),
  };
  writeArray(TASKS_KEY, tasks.map(task => task.id === taskId ? nextTask : task));
  return nextTask;
};

export const getCalendarTaskOverrides = (calendarId: string): CalendarTaskOccurrenceOverride[] => {
  return readArray<CalendarTaskOccurrenceOverride>(OVERRIDES_KEY).filter(item => item.calendarId === calendarId);
};

export const saveCalendarTaskOccurrenceOverride = (
  calendarId: string,
  taskId: string,
  occurrenceDate: string,
  data: CalendarTaskOccurrenceOverride['data'],
  isDeleted = false
): CalendarTaskOccurrenceOverride => {
  const now = new Date().toISOString();
  const safeData = sanitizeOccurrenceOverrideData(data);
  const overrides = readArray<CalendarTaskOccurrenceOverride>(OVERRIDES_KEY);
  const existing = overrides.find(item => (
    item.calendarId === calendarId &&
    item.taskId === taskId &&
    item.occurrenceDate === occurrenceDate
  ));
  const nextOverride: CalendarTaskOccurrenceOverride = {
    id: existing?.id || `${taskId}::${occurrenceDate}`,
    calendarId,
    taskId,
    occurrenceDate,
    data: safeData,
    isDeleted,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
  };
  writeArray(OVERRIDES_KEY, [
    nextOverride,
    ...overrides.filter(item => item.id !== nextOverride.id),
  ]);
  return nextOverride;
};

export const getCalendarLabels = (calendarId: string): CalendarTaskLabel[] => {
  return readArray<CalendarTaskLabel & { calendarId?: string }>(LABELS_KEY)
    .filter(label => label.calendarId === calendarId)
    .map(({ calendarId: _calendarId, ...label }) => label);
};

export const createCalendarLabel = (calendarId: string, name: string, color: string): CalendarTaskLabel => {
  const label = { id: makeId(), calendarId, name, color };
  const labels = readArray<CalendarTaskLabel & { calendarId?: string }>(LABELS_KEY);
  writeArray(LABELS_KEY, [label, ...labels]);
  const { calendarId: _calendarId, ...plainLabel } = label;
  return plainLabel;
};

export const getCalendarTasksOnline = async (calendarId: string): Promise<CalendarTask[]> => {
  if (!canUseCalendarApi()) return getCalendarTasks(calendarId);

  try {
    const localTasks = getCalendarTasks(calendarId);
    const response = await client.get<CalendarTask[]>('/calendar-tasks/', { params: { calendar_id: calendarId } });
    if (!response.data.length && localTasks.length) {
      return await Promise.all(localTasks.map(task => saveCalendarTaskOnline(calendarId, task)));
    }
    replaceCalendarItems(TASKS_KEY, calendarId, response.data);
    return response.data;
  } catch {
    return getCalendarTasks(calendarId);
  }
};

export const saveCalendarTaskOnline = async (
  calendarId: string,
  task: CalendarTaskSaveInput
): Promise<CalendarTask> => {
  if (!canUseCalendarApi()) return saveCalendarTask(calendarId, task);

  try {
    const payload = toCalendarTaskPayload(calendarId, task);
    if (task.id) {
      try {
        const response = await client.patch<CalendarTask>(`/calendar-tasks/${encodeURIComponent(task.id)}/`, payload);
        saveCalendarTask(calendarId, response.data);
        return response.data;
      } catch (error) {
        if ((error as any)?.response?.status !== 404) throw error;
      }
    }

    const response = await client.post<CalendarTask>('/calendar-tasks/', payload);
    saveCalendarTask(calendarId, response.data);
    return response.data;
  } catch {
    return saveCalendarTask(calendarId, task);
  }
};

export const updateCalendarTaskOnline = async (
  calendarId: string,
  taskId: string,
  data: Partial<CalendarTask>
): Promise<CalendarTask | null> => {
  if (!canUseCalendarApi()) return updateCalendarTask(calendarId, taskId, data);

  try {
    const response = await client.patch<CalendarTask>(
      `/calendar-tasks/${encodeURIComponent(taskId)}/`,
      toCalendarTaskPayload(calendarId, data)
    );
    saveCalendarTask(calendarId, response.data);
    return response.data;
  } catch {
    return updateCalendarTask(calendarId, taskId, data);
  }
};

export const deleteCalendarTaskOnline = async (calendarId: string, taskId: string) => {
  if (!canUseCalendarApi()) {
    deleteCalendarTask(calendarId, taskId);
    return;
  }

  try {
    await client.delete(`/calendar-tasks/${encodeURIComponent(taskId)}/`);
  } catch {
    // Keep local fallback behavior below.
  }
  deleteCalendarTask(calendarId, taskId);
};

export const getCalendarTaskOverridesOnline = async (calendarId: string): Promise<CalendarTaskOccurrenceOverride[]> => {
  if (!canUseCalendarApi()) return getCalendarTaskOverrides(calendarId);

  try {
    const localOverrides = getCalendarTaskOverrides(calendarId);
    const response = await client.get<CalendarTaskOccurrenceOverride[]>('/calendar-task-overrides/', {
      params: { calendar_id: calendarId },
    });
    if (!response.data.length && localOverrides.length) {
      return await Promise.all(localOverrides.map(override => (
        saveCalendarTaskOccurrenceOverrideOnline(
          calendarId,
          override.taskId,
          override.occurrenceDate,
          override.data,
          override.isDeleted
        )
      )));
    }
    replaceCalendarItems(OVERRIDES_KEY, calendarId, response.data);
    return response.data;
  } catch {
    return getCalendarTaskOverrides(calendarId);
  }
};

export const saveCalendarTaskOccurrenceOverrideOnline = async (
  calendarId: string,
  taskId: string,
  occurrenceDate: string,
  data: CalendarTaskOccurrenceOverride['data'],
  isDeleted = false
): Promise<CalendarTaskOccurrenceOverride> => {
  if (!canUseCalendarApi()) {
    return saveCalendarTaskOccurrenceOverride(calendarId, taskId, occurrenceDate, data, isDeleted);
  }

  try {
    const payload = {
      id: `${taskId}::${occurrenceDate}`,
      calendarId,
      taskId,
      occurrenceDate,
      data: sanitizeOccurrenceOverrideData(data),
      isDeleted,
    };
    const response = await client.post<CalendarTaskOccurrenceOverride>('/calendar-task-overrides/', payload);
    saveCalendarTaskOccurrenceOverride(calendarId, taskId, occurrenceDate, response.data.data, response.data.isDeleted);
    return response.data;
  } catch {
    return saveCalendarTaskOccurrenceOverride(calendarId, taskId, occurrenceDate, data, isDeleted);
  }
};

export const getCalendarLabelsOnline = async (calendarId: string): Promise<CalendarTaskLabel[]> => {
  if (!canUseCalendarApi()) return getCalendarLabels(calendarId);

  try {
    const localLabels = getCalendarLabels(calendarId);
    const response = await client.get<CalendarTaskLabel[]>('/calendar-task-labels/', { params: { calendar_id: calendarId } });
    if (!response.data.length && localLabels.length) {
      return await Promise.all(localLabels.map(label => (
        createCalendarLabelOnline(calendarId, label.name, label.color, label.id)
      )));
    }
    replaceCalendarItems(
      LABELS_KEY,
      calendarId,
      response.data.map(label => ({ ...label, calendarId }))
    );
    return response.data.map(({ calendarId: _calendarId, ...label }) => label);
  } catch {
    return getCalendarLabels(calendarId);
  }
};

export const createCalendarLabelOnline = async (
  calendarId: string,
  name: string,
  color: string,
  id?: string
): Promise<CalendarTaskLabel> => {
  if (!canUseCalendarApi()) return createCalendarLabel(calendarId, name, color);

  try {
    const response = await client.post<CalendarTaskLabel>('/calendar-task-labels/', { id, calendarId, name, color });
    const labels = readArray<CalendarTaskLabel & { calendarId?: string }>(LABELS_KEY);
    writeArray(LABELS_KEY, [
      { ...response.data, calendarId },
      ...labels.filter(item => item.id !== response.data.id),
    ]);
    const { calendarId: _calendarId, ...plainLabel } = response.data;
    return plainLabel;
  } catch {
    return createCalendarLabel(calendarId, name, color);
  }
};

export const getCalendarTaskOccurrencesOnline = async (
  calendarId: string,
  start: string,
  end: string
): Promise<CalendarTaskOccurrence[]> => {
  if (!canUseCalendarApi()) return [];

  try {
    const response = await client.get<CalendarTaskOccurrence[]>('/calendar-occurrences/', {
      params: { calendar_id: calendarId, start, end },
    });
    return response.data;
  } catch {
    return [];
  }
};

export const completeCalendarTaskOccurrenceOnline = async (
  calendarId: string,
  taskId: string,
  occurrenceDate: string,
  isCompleted?: boolean
): Promise<CalendarTaskOccurrence | null> => {
  if (!canUseCalendarApi()) return null;

  try {
    const response = await client.post<{ occurrence: CalendarTaskOccurrence }>('/calendar-occurrences/complete/', {
      calendarId,
      taskId,
      occurrenceDate,
      isCompleted,
    });
    return response.data.occurrence;
  } catch {
    return null;
  }
};

export const moveCalendarTaskOccurrenceOnline = async (
  calendarId: string,
  taskId: string,
  occurrenceDate: string,
  startAt: string
): Promise<{ occurrence: CalendarTaskOccurrence | null; collisionAdjusted: boolean }> => {
  if (!canUseCalendarApi()) return { occurrence: null, collisionAdjusted: false };

  try {
    const response = await client.post<{ occurrence: CalendarTaskOccurrence; collisionAdjusted: boolean }>('/calendar-occurrences/move/', {
      calendarId,
      taskId,
      occurrenceDate,
      startAt,
    });
    return response.data;
  } catch {
    return { occurrence: null, collisionAdjusted: false };
  }
};

export const resizeCalendarTaskOccurrenceOnline = async (
  calendarId: string,
  taskId: string,
  occurrenceDate: string,
  startAt: string,
  durationMinutes: number
): Promise<CalendarTaskOccurrence | null> => {
  if (!canUseCalendarApi()) return null;

  try {
    const response = await client.post<{ occurrence: CalendarTaskOccurrence }>('/calendar-occurrences/resize/', {
      calendarId,
      taskId,
      occurrenceDate,
      startAt,
      durationMinutes,
    });
    return response.data.occurrence;
  } catch {
    return null;
  }
};

export const saveCalendarTaskOccurrenceOnline = async (
  calendarId: string,
  taskId: string,
  occurrenceDate: string,
  data: CalendarTaskOccurrenceOverride['data']
): Promise<CalendarTaskOccurrence | null> => {
  if (!canUseCalendarApi()) {
    saveCalendarTaskOccurrenceOverride(calendarId, taskId, occurrenceDate, data);
    return null;
  }

  try {
    const response = await client.post<{ occurrence: CalendarTaskOccurrence }>('/calendar-occurrences/save/', {
      calendarId,
      taskId,
      occurrenceDate,
      data: sanitizeOccurrenceOverrideData(data),
    });
    return response.data.occurrence;
  } catch {
    saveCalendarTaskOccurrenceOverride(calendarId, taskId, occurrenceDate, data);
    return null;
  }
};

export const deleteCalendarTaskOccurrenceOnline = async (
  calendarId: string,
  taskId: string,
  occurrenceDate: string
) => {
  if (!canUseCalendarApi()) {
    saveCalendarTaskOccurrenceOverride(calendarId, taskId, occurrenceDate, {}, true);
    return;
  }

  try {
    await client.post('/calendar-occurrences/delete/', {
      calendarId,
      taskId,
      occurrenceDate,
    });
  } catch {
    saveCalendarTaskOccurrenceOverride(calendarId, taskId, occurrenceDate, {}, true);
  }
};

export const findNearestFreeStart = (tasks: CalendarTask[], durationMinutes: number): string => {
  const start = new Date();
  start.setMinutes(start.getMinutes() < 30 ? 30 : 0, 0, 0);
  if (start.getMinutes() === 0) start.setHours(start.getHours() + 1);

  for (let attempt = 0; attempt < 24 * 14; attempt += 1) {
    const candidate = new Date(start);
    candidate.setHours(start.getHours() + attempt);
    const candidateEnd = new Date(candidate.getTime() + durationMinutes * 60000);
    const hasCollision = tasks.some(task => {
      const taskStart = new Date(task.startAt);
      const taskEnd = new Date(taskStart.getTime() + task.durationMinutes * 60000);
      return candidate < taskEnd && candidateEnd > taskStart;
    });
    if (!hasCollision) return candidate.toISOString();
  }

  return start.toISOString();
};

export const doCalendarTasksOverlap = (
  leftStartAt: string,
  leftDurationMinutes: number,
  rightStartAt: string,
  rightDurationMinutes: number
) => {
  const leftStart = new Date(leftStartAt);
  const rightStart = new Date(rightStartAt);
  if (Number.isNaN(leftStart.getTime()) || Number.isNaN(rightStart.getTime())) return false;
  const leftEnd = new Date(leftStart.getTime() + leftDurationMinutes * 60000);
  const rightEnd = new Date(rightStart.getTime() + rightDurationMinutes * 60000);
  return leftStart < rightEnd && leftEnd > rightStart;
};

export const hasCalendarTaskCollision = (
  tasks: CalendarTask[],
  startAt: string,
  durationMinutes: number,
  excludeTaskId?: string
) => tasks.some(task => (
  task.id !== excludeTaskId &&
  doCalendarTasksOverlap(startAt, durationMinutes, task.startAt, task.durationMinutes)
));

export const findNearestFreeStartFrom = (
  tasks: CalendarTask[],
  durationMinutes: number,
  preferredStartAt: string,
  excludeTaskId?: string
): string => {
  const preferred = new Date(preferredStartAt);
  const start = Number.isNaN(preferred.getTime()) ? new Date() : preferred;
  start.setSeconds(0, 0);

  for (let attempt = 0; attempt < 24 * 14 * 2; attempt += 1) {
    const candidate = new Date(start.getTime() + attempt * 30 * 60000);
    const candidateAt = candidate.toISOString();
    if (!hasCalendarTaskCollision(tasks, candidateAt, durationMinutes, excludeTaskId)) return candidateAt;
  }

  return start.toISOString();
};

export const buildCalendarTaskDraftFromCard = (card: {
  id: number;
  title: string;
  description?: string;
  due_date?: string | null;
  card_color?: string;
  labelIds?: string[];
  checklists?: CalendarChecklist[];
  attachments?: CalendarAttachment[];
}): Partial<CalendarTask> => ({
  title: card.title,
  description: card.description || '',
  deadlineAt: card.due_date || undefined,
  headerColor: card.card_color || undefined,
  sourceBoardCardId: card.id,
  labelIds: card.labelIds || [],
  checklists: card.checklists || [],
  attachments: card.attachments || [],
});

export const getAttachmentDisplayName = (attachment: CalendarAttachment) => {
  const rawName = attachment.name || attachment.url || '';
  const withoutQuery = rawName.split('?')[0];
  return withoutQuery.split('/').filter(Boolean).pop() || rawName || 'attachment';
};
